import React, { FC, useState } from 'react';
import { RootState } from '../../../../rootReducer';
import { connect } from 'react-redux';
import {
  FindingTypeProperty,
  FindingTypeTemplateDetails,
  moveToNextPage,
  moveToPreviousPage,
  CreateFindingTypeRuleWithIndex,
  populateRules,
  FindingTypeDetails
} from '../AnnotationStandardForm.slice';
import Actions from '../Actions/Actions';
import { AppDispatch } from '../../../../store';
import FindingTypeRuleRow from './FindingTypeRuleRow';
import NewRuleRow from './NewRuleRow';
import rootStyles from '../AnnotationStandardForm.module.scss';
import { validateDuplicatedRows } from '../helpers';
import ImportComponent from '../../../MaintenanceTriggers/MaintenanceTrigger/ImportCSV';
import {
  AnnotationStandardImportRule,
  mapAnnotationStandardImportRules,
  propertyNameToRuleKey,
  validateAnnotationStandardImportRuleRow
} from '../../../MaintenanceTriggers/MaintenanceTrigger/ImportHelpers';
import CSVFile from '../../../../assets/templates/asTemplate.csv';

type Props = {
  selectedFindingTypes: Array<FindingTypeTemplateDetails>;
  populatedFindingTypes: Array<FindingTypeDetails>;
  canEdit: boolean;
  onMoveToNextPage: () => void;
  onMoveToPreviousPage: () => void;
  onPopulateRules: (rules: Array<CreateFindingTypeRuleWithIndex>) => void;
};

const Rules: FC<Props> = ({ selectedFindingTypes, populatedFindingTypes, onMoveToNextPage, onMoveToPreviousPage, onPopulateRules, canEdit }) => {
  const [rules, setRules] = useState<Array<CreateFindingTypeRuleWithIndex>>(() => {
    const rules: Array<CreateFindingTypeRuleWithIndex> = [];

    for (const type of populatedFindingTypes) {
      const selectedFindingTypeTemplate = selectedFindingTypes.find(x => x.id === type.findingTypeTemplateId);
      if (!selectedFindingTypeTemplate) {
        continue;
      }

      const newRules: CreateFindingTypeRuleWithIndex[] = [];
      let i = 0;

      for (const rule of type.rules) {
        const populatedProprtyIds = rule.items.map(x => x.findingTypePropertyId);
        const allPropertyIds = selectedFindingTypeTemplate.properties.map(x => x.id);

        const removedPropertyIds = populatedProprtyIds.filter(x => !allPropertyIds.includes(x));
        const addedPropertyIds = allPropertyIds.filter(x => !populatedProprtyIds.includes(x));

        const indexedRule: CreateFindingTypeRuleWithIndex = {
          ...{
            ...rule,
            items: [
              ...rule.items,
              ...addedPropertyIds.map(x => ({
                findingTypeRuleId: rule.items.length ? rule.items[0].findingTypeRuleId : undefined,
                findingTypePropertyId: x,
                value: ''
              }))
            ],
            errorMark: false
          },
          index: i++
        };
        newRules.push(indexedRule);
      }

      rules.push(...newRules);
    }

    return rules;
  });

  const handleChange = (index: number, findingTypeTemplateId: string, findingTypePropertyId: string, value: string) => {
    let newRules = rules.map(x => {
      if (x.index === index && x.findingTypeTemplateId === findingTypeTemplateId) {
        return {
          ...x,
          items: x.items.map(y => {
            if (y.findingTypePropertyId === findingTypePropertyId) {
              return {
                ...y,
                value
              };
            }

            return y;
          })
        };
      }

      return x;
    });

    newRules = validateDuplicatedRows(newRules, selectedFindingTypes, populatedFindingTypes);

    setRules(newRules);
  };

  const removeClicked = (index: number, findingTypeTemplateId: string) => {
    let newRules = [...rules.filter(x => !(x.index === index && x.findingTypeTemplateId === findingTypeTemplateId))];
    newRules = validateDuplicatedRows(newRules, selectedFindingTypes, populatedFindingTypes);
    setRules(newRules);
  };

  const onAddRow = (findingTypeTemplateId: string) => {
    const max = Math.max(...rules.filter(x => x.findingTypeTemplateId === findingTypeTemplateId).map(x => x.index));
    const type = populatedFindingTypes.find(x => x.findingTypeTemplateId === findingTypeTemplateId)!;
    const template = selectedFindingTypes.find(x => x.id === findingTypeTemplateId);

    const populatedProprtyIds = type.representations.map(x => x.findingTypePropertyId);
    const allPropertyIds = template!.properties.map(x => x.id);
    const addedPropertyIds = allPropertyIds.filter(x => !populatedProprtyIds.includes(x));

    setRules([
      ...rules,
      {
        index: max === -Infinity ? 0 : max + 1,
        findingTypeTemplateId: findingTypeTemplateId,
        items: [
          ...type.representations.map(x => ({
            findingTypePropertyId: x.findingTypePropertyId,
            value: ''
          })),
          ...addedPropertyIds.map(id => ({
            findingTypePropertyId: id,
            value: ''
          }))
        ],
        errorMark: false
      }
    ]);
  };

  const onNextPage = () => {
    onPopulateRules(rules);
    onMoveToNextPage();
  };

  const onPreviousPage = () => {
    onMoveToPreviousPage();
  };

  const isValid = () => !rules.some(x => x.errorMark);

  const onRulesImport = (newRules: AnnotationStandardImportRule[]) => {
    let rulesToImport: CreateFindingTypeRuleWithIndex[] = [];
    const maxes = {} as { [key: string]: number };

    newRules.forEach((rule, index) => {
      const findingTypeTemplateId = selectedFindingTypes.find(x => x.name === rule.findingType)?.id;

      if (!findingTypeTemplateId) {
        return;
      }

      const max = Math.max(...rules.filter(x => x.findingTypeTemplateId === findingTypeTemplateId).map(x => x.index));
      if (maxes[findingTypeTemplateId] === undefined) {
        maxes[findingTypeTemplateId] = max;
      }
      const type = populatedFindingTypes.find(x => x.findingTypeTemplateId === findingTypeTemplateId)!;
      const template = selectedFindingTypes.find(x => x.id === findingTypeTemplateId);

      const populatedProprtyIds = type.representations.map(x => x.findingTypePropertyId);
      const allPropertyIds = template!.properties.map(x => x.id);
      const addedPropertyIds = allPropertyIds.filter(x => !populatedProprtyIds.includes(x));

      const getRuleValue = (findingTypePropertyId: string, properties: FindingTypeProperty[]) => {
        const property = properties.find(x => x.id === findingTypePropertyId);
        if (!property) {
          return '';
        }

        return propertyNameToRuleKey(property.name, rule);
      };

      rulesToImport.push({
        index: maxes[findingTypeTemplateId] === -Infinity ? 0 : ++maxes[findingTypeTemplateId],
        findingTypeTemplateId: findingTypeTemplateId,
        items: [
          ...type.representations.map(x => ({
            findingTypePropertyId: x.findingTypePropertyId,
            value: getRuleValue(x.findingTypePropertyId, template!.properties)
          })),
          ...addedPropertyIds.map(id => ({
            findingTypePropertyId: id,
            value: getRuleValue(id, template!.properties)
          }))
        ],
        errorMark: false
      });

      if (maxes[findingTypeTemplateId] === -Infinity) {
        maxes[findingTypeTemplateId] = 0;
      }
    });

    setRules([...rules, ...rulesToImport]);
  };

  const generateProperties = (properties: Array<FindingTypeProperty>, id: string) => {
    return rules
      .filter(x => x.findingTypeTemplateId === id)
      .map(rule => (
        <FindingTypeRuleRow
          canEdit={canEdit}
          key={rule.index}
          findingTypeId={id}
          properties={properties}
          rule={rule}
          removeClicked={removeClicked}
          onHandleChange={handleChange}
        />
      ));
  };

  const generateTypes = () => {
    return (
      <div>
        {canEdit && <ImportComponent
          onFileParsed={(rawData: any[]) => {
            const data = mapAnnotationStandardImportRules(rawData);

            // Handle the imported data
            validateAnnotationStandardImportRuleRow(data, selectedFindingTypes);

            onRulesImport(data);
          }}
          onDownloadTemplateClicked={() => {
            const link = document.createElement('a');
            link.href = CSVFile;
            link.download = 'AnnotationStandardRulesTemplate.csv';
            link.click();
          }}
          onClearAllClicked={() => setRules([])}
        />}
        <div className={rootStyles.contentContainer}>
          {selectedFindingTypes.some(x => x.properties.some(y => y.values !== '')) ? (
            selectedFindingTypes.map((findingType, index) => (
              <div key={findingType.id} style={{ marginTop: index !== 0 ? '2rem' : 0 }}>
                <div style={{ textAlign: 'center' }}>
                  <h4>{findingType.isMLRelated ? "(ML) " : ""}{findingType.name}</h4>
                </div>
                <table className='custom-table' style={{ margin: '0 auto' }}>
                  <thead>
                    <tr>
                      {findingType.properties.map(x => (
                        <th key={x.id}>{x.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{generateProperties(findingType.properties, findingType.id)}</tbody>
                </table>
                <NewRuleRow canEdit={canEdit} findingTypeId={findingType.id} properties={findingType.properties} onAddNewRow={onAddRow} />
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', marginTop: '35%' }}>There is no finding type properties with predifined values</div>
          )}
        </div>
        <Actions isLoading={false} onNext={onNextPage} onPrevious={onPreviousPage} isDisabled={!isValid()} />
      </div>
    );
  };

  return <div>{generateTypes()}</div>;
};

const mapStateToProps = (state: RootState) => ({
  selectedFindingTypes: state.annotationStandardForm.selectedFindingTypeTemplates,
  populatedFindingTypes: state.annotationStandardForm.annotationStandard.findingTypes
    ? state.annotationStandardForm.annotationStandard.findingTypes
    : [],
  canEdit: state.annotationStandardForm.canEdit
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onMoveToNextPage: () => dispatch(moveToNextPage()),
  onMoveToPreviousPage: () => dispatch(moveToPreviousPage()),
  onPopulateRules: (rules: Array<CreateFindingTypeRuleWithIndex>) => dispatch(populateRules(rules))
});

export default connect(mapStateToProps, mapDispatchToProps)(Rules);
